/**
 * Workbook Brain Writer Service
 * Provides write operations for Workbook Brain features
 * Enables Workbook Brain to modify the workbook, not just analyze it
 */

import { writeValues, insertFormula } from '../office/writer';
import type { OptimizationPriority } from '@excel-ai-studio/shared';

/* global Excel */

/**
 * Apply optimization to workbook
 */
export async function applyOptimization(priority: OptimizationPriority): Promise<{
  success: boolean;
  message: string;
  affectedCells?: number;
}> {
  try {
    // Delegate to optimization executor
    const { implementOptimization } = await import('./optimization-executor.service');
    return await implementOptimization(priority);
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    throw new Error(`Impossible d'appliquer l'optimisation: ${msg}`);
  }
}

/**
 * Export documentation to new sheet
 */
export async function exportDocumentationToSheet(
  title: string,
  content: string[]
): Promise<{ success: boolean; message: string }> {
  try {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel context not available');
    }

    return await Excel.run(async (ctx) => {
      try {
        // Vérifier que le workbook n'est pas en lecture seule
        const workbook = ctx.workbook;
        
        // Essayer de créer une feuille temporaire pour tester l'accès
        let testSheet: any = null;
        try {
          testSheet = workbook.worksheets.add('_Test_');
          await ctx.sync();
          testSheet.delete();
          await ctx.sync();
        } catch (testError) {
          const msg = testError instanceof Error ? testError.message : String(testError);
          if (msg.includes('ReadOnly') || msg.includes('read-only')) {
            throw new Error('Le classeur est en lecture seule. Impossible de créer une feuille.');
          }
          throw testError;
        }

        // Vérifier si la feuille existe déjà
        const existingSheet = workbook.worksheets.getItemOrNullObject('Documentation');
        await ctx.sync();
        
        if (!existingSheet.isNullObject) {
          existingSheet.delete();
          await ctx.sync();
        }

        // Créer la feuille de documentation
        const sheet = workbook.worksheets.add('Documentation');

        // Ajouter titre
        const titleCell = sheet.getRange('A1');
        titleCell.values = [[title]];
        titleCell.format.font.bold = true;
        titleCell.format.font.size = 14;
        titleCell.format.fill.color = '#E7E6E6';

        // Ajouter contenu
        let row = 3;
        for (const line of content) {
          const cell = sheet.getRange(`A${row}`);
          cell.values = [[line]];
          cell.format.wrapText = true;
          row++;
        }

        // Définir la largeur de colonne
        sheet.getRange('A:A').format.columnWidth = 50;

        sheet.activate();
        await ctx.sync();
        
        return {
          success: true,
          message: `Documentation exportée dans la feuille "Documentation"`,
        };
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(`Erreur lors de la création de la feuille: ${msg}`);
      }
    });
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    console.error('[Workbook Brain Writer] Erreur:', msg);
    throw new Error(`Impossible d'exporter la documentation: ${msg}`);
  }
}

/**
 * Create risk mitigation sheet
 */
export async function createRiskMitigationSheet(risks: any[]): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    // Vérifier que Excel est disponible
    if (typeof Excel === 'undefined') {
      throw new Error('Excel context not available');
    }

    return await Excel.run(async (ctx) => {
      try {
        // Vérifier que le workbook n'est pas en lecture seule
        const workbook = ctx.workbook;
        
        // Essayer de créer une feuille temporaire pour tester l'accès
        let testSheet: any = null;
        try {
          testSheet = workbook.worksheets.add('_Test_');
          await ctx.sync();
          testSheet.delete();
          await ctx.sync();
        } catch (testError) {
          const msg = testError instanceof Error ? testError.message : String(testError);
          if (msg.includes('ReadOnly') || msg.includes('read-only')) {
            throw new Error('Le classeur est en lecture seule. Impossible de créer une feuille.');
          }
          throw testError;
        }

        // Vérifier si la feuille existe déjà
        const existingSheet = workbook.worksheets.getItemOrNullObject('Risques');
        await ctx.sync();
        
        if (!existingSheet.isNullObject) {
          existingSheet.delete();
          await ctx.sync();
        }

        // Créer la feuille de risques
        const sheet = workbook.worksheets.add('Risques');
        
        // Ajouter titre
        const titleCell = sheet.getRange('A1');
        titleCell.values = [['Mitigation des Risques']];
        titleCell.format.font.bold = true;
        titleCell.format.font.size = 14;
        titleCell.format.fill.color = '#FF6B6B';
        
        // Ajouter en-têtes
        const headerRow = sheet.getRange('A3:D3');
        headerRow.values = [['Risque', 'Sévérité', 'Impact', 'Mitigation']];
        headerRow.format.font.bold = true;
        headerRow.format.fill.color = '#E7E6E6';
        
        // Ajouter les risques
        let row = 4;
        for (const risk of risks) {
          const riskRow = sheet.getRange(`A${row}:D${row}`);
          riskRow.values = [[
            risk.title || 'Sans titre',
            risk.severity || 'Moyen',
            risk.impact || 'Non spécifié',
            risk.mitigation || 'À définir'
          ]];
          row++;
        }
        
        // Ajuster les largeurs de colonnes
        sheet.getRange('A:A').format.columnWidth = 30;
        sheet.getRange('B:B').format.columnWidth = 15;
        sheet.getRange('C:C').format.columnWidth = 20;
        sheet.getRange('D:D').format.columnWidth = 30;
        
        sheet.activate();
        await ctx.sync();
        
        return {
          success: true,
          message: `Feuille "Risques" créée avec ${risks.length} risques`,
        };
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(`Erreur lors de la création de la feuille: ${msg}`);
      }
    });
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    console.error('[Workbook Brain Writer] Erreur:', msg);
    throw new Error(`Impossible de créer la feuille de risques: ${msg}`);
  }
}

/**
 * Create performance improvement sheet
 */
export async function createPerformanceSheet(improvements: string[]): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel context not available');
    }

    return await Excel.run(async (ctx) => {
      try {
        // Vérifier que le workbook n'est pas en lecture seule
        const workbook = ctx.workbook;
        
        // Essayer de créer une feuille temporaire pour tester l'accès
        let testSheet: any = null;
        try {
          testSheet = workbook.worksheets.add('_Test_');
          await ctx.sync();
          testSheet.delete();
          await ctx.sync();
        } catch (testError) {
          const msg = testError instanceof Error ? testError.message : String(testError);
          if (msg.includes('ReadOnly') || msg.includes('read-only')) {
            throw new Error('Le classeur est en lecture seule. Impossible de créer une feuille.');
          }
          throw testError;
        }

        // Vérifier si la feuille existe déjà
        const existingSheet = workbook.worksheets.getItemOrNullObject('Améliorations Performance');
        await ctx.sync();
        
        if (!existingSheet.isNullObject) {
          existingSheet.delete();
          await ctx.sync();
        }

        // Créer la feuille
        const sheet = workbook.worksheets.add('Améliorations Performance');

        // Ajouter titre
        const titleCell = sheet.getRange('A1');
        titleCell.values = [['Améliorations de Performance']];
        titleCell.format.font.bold = true;
        titleCell.format.font.size = 14;
        titleCell.format.fill.color = '#E7E6E6';

        // Ajouter améliorations
        let row = 3;
        for (const improvement of improvements) {
          const cell = sheet.getRange(`A${row}`);
          cell.values = [[`• ${improvement}`]];
          cell.format.wrapText = true;
          row++;
        }

        // Définir la largeur de colonne
        sheet.getRange('A:A').format.columnWidth = 50;

        sheet.activate();
        await ctx.sync();
        
        return {
          success: true,
          message: `Feuille d'améliorations de performance créée (${improvements.length} améliorations)`,
        };
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        throw new Error(`Erreur lors de la création de la feuille: ${msg}`);
      }
    });
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    console.error('[Workbook Brain Writer] Erreur:', msg);
    throw new Error(`Impossible de créer la feuille de performance: ${msg}`);
  }
}

/**
 * Create quick wins sheet
 */
export async function createQuickWinsSheet(quickWins: string[]): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.add('Gains Rapides');

      // Add title
      const titleCell = sheet.getRange('A1');
      titleCell.values = [['Gains Rapides - Actions Immédiates']];
      titleCell.format.font.bold = true;
      titleCell.format.font.size = 14;
      titleCell.format.fill.color = '#C6E0B4';

      // Add quick wins
      let row = 3;
      for (const win of quickWins) {
        const cell = sheet.getRange(`A${row}`);
        cell.values = [[`✓ ${win}`]];
        cell.format.wrapText = true;
        row++;
      }

      // Set column width
      sheet.getRange('A:A').format.columnWidth = 50;

      sheet.activate();
      await ctx.sync();
    });

    return {
      success: true,
      message: `Feuille des gains rapides créée (${quickWins.length} actions)`,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    throw new Error(`Impossible de créer la feuille des gains rapides: ${msg}`);
  }
}

/**
 * Apply chat suggestion to workbook
 */
export async function applyChatSuggestion(
  suggestion: string,
  data?: unknown[][]
): Promise<{ success: boolean; message: string }> {
  try {
    if (data && data.length > 0) {
      await writeValues(data);
      return {
        success: true,
        message: `Suggestion appliquée: ${suggestion}`,
      };
    }

    return {
      success: false,
      message: 'Aucune donnée à appliquer',
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    throw new Error(`Impossible d'appliquer la suggestion: ${msg}`);
  }
}

/**
 * Create analysis summary sheet
 */
export async function createAnalysisSummarySheet(summary: {
  title: string;
  sections: Array<{ heading: string; content: string[] }>;
}): Promise<{ success: boolean; message: string }> {
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.add('Résumé Analyse');

      // Add title
      const titleCell = sheet.getRange('A1');
      titleCell.values = [[summary.title]];
      titleCell.format.font.bold = true;
      titleCell.format.font.size = 14;
      titleCell.format.fill.color = '#D9E1F2';

      // Add sections
      let row = 3;
      for (const section of summary.sections) {
        // Section heading
        const headingCell = sheet.getRange(`A${row}`);
        headingCell.values = [[section.heading]];
        headingCell.format.font.bold = true;
        headingCell.format.fill.color = '#E7E6E6';
        row++;

        // Section content
        for (const line of section.content) {
          const cell = sheet.getRange(`A${row}`);
          cell.values = [[`  • ${line}`]];
          cell.format.wrapText = true;
          row++;
        }

        row++; // Empty line between sections
      }

      // Set column width
      sheet.getRange('A:A').format.columnWidth = 50;

      sheet.activate();
      await ctx.sync();
    });

    return {
      success: true,
      message: `Résumé d'analyse créé`,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    throw new Error(`Impossible de créer le résumé d'analyse: ${msg}`);
  }
}

/**
 * Create recommendations sheet
 */
export async function createRecommendationsSheet(recommendations: Array<{
  title: string;
  description: string;
  priority: string;
  effort: string;
}>): Promise<{ success: boolean; message: string }> {
  try {
    await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.add('Recommandations');

      // Add headers
      const headers = ['Recommandation', 'Description', 'Priorité', 'Effort'];
      const headerRange = sheet.getRange('A1:D1');
      headerRange.values = [headers];
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = '#FFC000';
      headerRange.format.font.color = '#000000';

      // Add recommendations
      let row = 2;
      for (const rec of recommendations) {
        const recRange = sheet.getRange(`A${row}:D${row}`);
        recRange.values = [
          [
            rec.title || 'Sans titre',
            rec.description || '',
            rec.priority || 'Moyenne',
            rec.effort || 'Moyen',
          ],
        ];
        row++;
      }

      // Set column widths
      sheet.getRange('A:A').format.columnWidth = 25;
      sheet.getRange('B:B').format.columnWidth = 35;
      sheet.getRange('C:C').format.columnWidth = 15;
      sheet.getRange('D:D').format.columnWidth = 15;

      sheet.activate();
      await ctx.sync();
    });

    return {
      success: true,
      message: `Feuille de recommandations créée (${recommendations.length} recommandations)`,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    throw new Error(`Impossible de créer la feuille de recommandations: ${msg}`);
  }
}
